import type { ReactNode } from "react";
import { formatDate, formatSameDayDelivery } from "@/utils/formateData";
import { getDaysDifference } from "@/utils/getDaysDifference";
import { TResponseOrderDetails } from "@/types/projectType";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";

type TCreatorPayout = {
  currency?: string;
  totalPrice?: number;
  videos?: {
    video_id: string;
    price: number;
    additionalPrice?: number;
    currency: string;
    deliverables?: string;
  }[];
};

interface DataItemProps {
  title: string;
  children: ReactNode;
}

const DataItem = ({ title, children }: DataItemProps) => (
  <div className="min-w-0 space-y-1.5">
    <h5 className="text-sm text-[#98A2B3] font-normal leading-4 tracking-[-0.01em]">
      {title}
    </h5>
    <div className="text-xs md:text-sm text-[#667085] leading-[18px] tracking-[-0.01em] font-medium">
      {children}
    </div>
  </div>
);

const addUtcDays = (dateStr: string, days: number) => {
  const next = new Date(dateStr);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString();
};

const ProjectDetailsTable = ({
  data,
  creatorPayout,
}: {
  data: TResponseOrderDetails;
  creatorPayout?: TCreatorPayout;
}) => {
  const assignedVideos = data?.orderVideos ?? [];
  const assignedQuantity = assignedVideos.length;

  const assignedDeliveryDays = assignedVideos.reduce((total, video) => {
    return total + (video?.scriptDetails?.deliverySlot?.days || 0);
  }, 0);

  const orderQuantity = data?.orderPlanDetails?.quantity || assignedQuantity || 1;
  const fullDueDays = Number(
    getDaysDifference(data?.createdOn, data?.deadline) || 0,
  );
  const dueDays =
    assignedDeliveryDays > 0
      ? assignedDeliveryDays
      : assignedQuantity > 0 && orderQuantity > 0
        ? Math.round((assignedQuantity / orderQuantity) * fullDueDays)
        : fullDueDays;

  const assignedDeadline =
    dueDays > 0 && data?.createdOn
      ? addUtcDays(data.createdOn, dueDays)
      : data?.deadline;

  const startedOn = formatDate(data?.createdOn);
  const deliveryOn = data?.isSameDayFlow
    ? formatSameDayDelivery(data?.deadline)
    : formatDate(assignedDeadline);

  const orderStatus = data?.orderStatus?.isPending ? (
    <span className="bg-[#FBF1DA] text-[#714B00] px-2 py-1 rounded-full inline-block whitespace-nowrap">
      Pending
    </span>
  ) : data?.orderStatus?.isProgress ? (
    <span className="bg-[#FBF1DA] text-[#714B00] px-2 py-1 rounded-full inline-block whitespace-nowrap">
      In Progress
    </span>
  ) : data?.orderStatus?.isCompleted ? (
    <span className="bg-[#12B76A] text-white px-2 py-1 rounded-full inline-block whitespace-nowrap">
      Completed
    </span>
  ) : (
    "Unknown"
  );

  const payoutVideos = creatorPayout?.videos ?? [];
  const payoutAmount =
    creatorPayout?.totalPrice ??
    payoutVideos.reduce(
      (sum, item) => sum + (item.price || 0) + (item.additionalPrice || 0),
      0,
    );
  const payoutCurrency =
    creatorPayout?.currency ||
    payoutVideos[0]?.currency ||
    data?.paymentDetails?.currency;
  const isShootOnly =
    payoutVideos.length > 0 &&
    payoutVideos.every((item) => item.deliverables === "shoot_only");

  return (
    <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-x-6 gap-y-5 items-start">
      <DataItem title="Client">
        <p className="truncate" title={data?.clientId?.businessName}>
          {data?.clientId?.businessName}
        </p>
      </DataItem>
      <DataItem title="Industry">
        <p className="truncate" title={data?.clientId?.industry}>
          {data?.clientId?.industry}
        </p>
      </DataItem>
      <DataItem title="Quantity">
        {assignedQuantity} {assignedQuantity === 1 ? "video" : "videos"}
      </DataItem>
      <DataItem title="Started on">{startedOn}</DataItem>
      <DataItem title="Delivery on">{deliveryOn}</DataItem>
      <DataItem title="Status">{orderStatus}</DataItem>
      <DataItem title="Due time">
        <span className="whitespace-nowrap">{dueDays} days</span>
      </DataItem>
      {payoutVideos.length > 0 && (
        <DataItem title="Your payout">
          <span className="text-[#027143] font-semibold whitespace-nowrap">
            {getCurrencySymbol(payoutCurrency)} {payoutAmount}
          </span>
          {isShootOnly && (
            <span className="block text-[#98A2B3] text-xs mt-0.5">
              Shoot only
            </span>
          )}
        </DataItem>
      )}
    </div>
  );
};

export default ProjectDetailsTable;
